import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input } from "component-library";
import {
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/16/solid";
import { PraxisOverview } from "@shared/praxis-admin";
import { adminPraxisRoute, routes } from "../../routing/routes";
import { usePraxes } from "./hooks/usePraxes";
import { PageStatus } from "./components/admin-layout";

const matches = (praxis: PraxisOverview, search: string) =>
  [praxis.subdomain, praxis.domain, praxis.email, ...praxis.usernames].some(
    (value) => value.toLowerCase().includes(search),
  );

const PraxisRow: React.FC<{ praxis: PraxisOverview }> = ({ praxis }) => {
  const { t, i18n } = useTranslation();

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-primary md:flex-row md:items-center md:p-4">
      <Link
        to={adminPraxisRoute(praxis.subdomain)}
        className="flex flex-1 flex-col gap-1 focus:outline-none"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{praxis.subdomain}</span>
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-primary">
            {t("admin.praxes.submittedQuestionnaires", {
              count: praxis.submittedQuestionnaires,
            })}
          </span>
          {!praxis.hasConfig && (
            <span className="rounded-full bg-orange-500 px-2 py-0.5 text-xs font-medium text-white">
              {t("admin.praxes.defaultConfig")}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-x-4 text-sm text-gray-600 md:flex-row md:flex-wrap">
          <span>{praxis.email}</span>
          <span>
            {t("admin.praxes.login", {
              usernames: praxis.usernames.join(", ") || "–",
            })}
          </span>
          <span>
            {t("admin.praxes.createdAt", {
              date: new Date(praxis.createdAt).toLocaleDateString(
                i18n.language,
              ),
            })}
          </span>
        </div>
      </Link>
      <div className="flex items-center gap-2">
        <a
          href={`https://${praxis.domain}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-primary hover:bg-indigo-50"
        >
          {praxis.domain}
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
        <Link
          to={adminPraxisRoute(praxis.subdomain)}
          aria-label={t("admin.praxes.open", { subdomain: praxis.subdomain })}
          className="rounded-lg p-1.5 text-primary hover:bg-indigo-50"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </Link>
      </div>
    </li>
  );
};

export const AdminPraxesPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: praxes, isLoading, error } = usePraxes();
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      (praxes ?? []).filter((praxis) =>
        matches(praxis, search.trim().toLowerCase()),
      ),
    [praxes, search],
  );

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl text-text-primary">
          {t("admin.praxes.title")}
        </h1>
        <Link to={routes.adminNewPraxis}>
          <Button variant="contained" tabIndex={-1}>
            <PlusIcon className="h-4 w-4" />
            {t("admin.praxes.create")}
          </Button>
        </Link>
      </div>

      {isLoading && <PageStatus>{t("admin.loading")}</PageStatus>}
      {error && <PageStatus>{t("admin.praxes.loadError")}</PageStatus>}

      {praxes && praxes.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <BuildingOffice2Icon className="h-12 w-12 text-primary" />
          <p className="text-lg font-semibold text-gray-600">
            {t("admin.praxes.empty")}
          </p>
        </div>
      )}

      {praxes && praxes.length > 0 && (
        <>
          <Input
            type="search"
            placeholder={t("admin.praxes.search")}
            aria-label={t("admin.praxes.search")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="max-w-md"
          />
          <div className="text-sm text-gray-600">
            {t("admin.praxes.count", {
              count: filtered.length,
              total: praxes.length,
            })}
          </div>
          <ul className="flex flex-col gap-3">
            {filtered.map((praxis) => (
              <PraxisRow key={praxis.id} praxis={praxis} />
            ))}
          </ul>
        </>
      )}
    </>
  );
};
